import { DIFFICULTIES, configFor, isCanonical as isCanonicalRuleset } from '@blinkered/engine'
import type {
  Difficulty,
  FlipEconomy,
  GameConfig,
  KeyScheme,
  WordCompleteMode,
} from '@blinkered/engine'
import { DEFAULT_LOCALE, localeFor, preferredLocale } from '@blinkered/i18n'

/**
 * What the player has chosen, and what is remembered between visits.
 *
 * Two of these are not settings at all but game-time choices: `gameLanguage` decides what the
 * board is dealt from, and `difficulty` decides how fast it comes at you. A game cannot be
 * ranked against one that used different values for either, so both are locked while a game
 * runs and both live in the setup panel rather than in nerd mode.
 */
export interface Settings {
  /** The preset a game is based on, and the base for the custom ruleset. */
  readonly difficulty: Difficulty
  /**
   * True when the nerd-mode ruleset is the one in play.
   *
   * Separate from `overrides` being non-empty so the two can coexist: switching back to a preset
   * leaves the custom ruleset stored but inactive, and switching to `custom` picks it up again
   * rather than making the player re-enter it.
   */
  readonly custom: boolean
  /** Anything changed away from the preset. Applied only while `custom`. */
  readonly overrides: Partial<GameConfig>
  readonly keyScheme: KeyScheme
  /** Reveals every rule and the arithmetic behind it. Off by default. */
  readonly nerdMode: boolean
  /** The language the board is dealt and words are judged in. */
  readonly gameLanguage: string
  /**
   * The language the interface is written in.
   *
   * Kept apart from `gameLanguage` even though one control sets both, because they are
   * genuinely different questions: plenty of people would want to practice French with an
   * interface they read fluently, and merging the two fields now would be hard to undo later.
   */
  readonly uiLanguage: string
  /**
   * Whether the first-run tour has been dismissed for good.
   *
   * False until the player either finishes the tour with the box ticked or skips and ticks it, so
   * a first visit and a visit from somebody who closed the tab mid-tour both get it again. Its own
   * field rather than being inferred from anything else here: "has played before" and "has been
   * shown the rules" are different facts and only one of them is this one.
   */
  readonly tutorialSeen: boolean
  /**
   * Which palette the interface is drawn in.
   *
   * A device preference rather than an account one, deliberately: it is a fact about the screen
   * somebody is looking at -- a phone in sunlight, a laptop at night, eyes that want more
   * contrast -- and syncing it would mean the account overruling the room.
   */
  readonly theme: Theme
}

/**
 * The three palettes, and what each is for.
 *
 * - `traditional` is what the game has always looked like, and is the default.
 * - `light` is the same game in daylight, for a bright room and for anybody who finds a dark
 *   interface harder rather than easier to read.
 * - `contrast` is black and white with the accents pushed as far as they go: every pairing in it
 *   clears WCAG AA with room, and the borders clear 1.4.11's 3:1 rather than being suggestions.
 *
 * `styles.css` holds the values and `apps/web/test/themes.test.ts` holds the arithmetic, so a
 * colour cannot be changed into a failing one without a test saying so.
 */
export const THEMES = ['traditional', 'light', 'contrast'] as const
export type Theme = (typeof THEMES)[number]

/**
 * What the browser asks for, if it is one we have, and English otherwise.
 *
 * Used for both languages on a first visit. Guessing which language somebody wants to *play* a
 * word game in is a little presumptuous, but the picker sits at the top of the page and is
 * always live, so a wrong guess costs one click; an interface nobody can read costs more.
 */
export function guessLanguage(): string {
  try {
    return preferredLocale(navigator.languages)
  } catch {
    return DEFAULT_LOCALE
  }
}

export const DIFFICULTY_NAMES = Object.keys(DIFFICULTIES) as Difficulty[]
export const WORD_COMPLETE_MODES: readonly WordCompleteMode[] = ['shuffle', 'spend', 'keep']
export const FLIP_ECONOMIES: readonly FlipEconomy[] = [
  'none',
  'perLetter',
  'fibonacci',
  'overMinimum',
]
export const KEY_SCHEMES: readonly KeyScheme[] = ['cycle', 'advance']

/** The fifth option in the difficulty row, offered only once a custom ruleset exists. */
export const CUSTOM_RULES = 'custom'
export type Ruleset = Difficulty | typeof CUSTOM_RULES

/**
 * Whether the stored ruleset actually forks from a preset.
 *
 * Having overrides at all is not the same question, and treating it as the same is what put a
 * fifth chip on the setup panel for a ruleset identical to `medium`. Nerd mode writes a field
 * the moment it is touched, so a player who nudged a number and put it back had a custom
 * ruleset that was custom in name only.
 *
 * The comparison itself belongs to the engine now, because a server has to reach the same verdict
 * about the same game and two implementations would drift. This asks the engine's question about
 * the stored overrides, resolved whether or not they are currently applied: "does this ruleset
 * fork" is a fact about what is saved, not about what is switched on.
 */
export function hasCustomRules(settings: Settings): boolean {
  const language = settings.gameLanguage
  const rules = configFor(settings.difficulty, { ...settings.overrides, language })
  return !isCanonicalRuleset(rules, settings.difficulty)
}

/**
 * Which chip is lit: a preset, or the custom ruleset.
 *
 * Follows what is offered. A custom ruleset that matches a preset is not offered, so it cannot
 * be the one lit either, and the preset it matches is what the player is actually playing.
 */
export function rulesetOf(settings: Settings): Ruleset {
  return settings.custom && hasCustomRules(settings) ? CUSTOM_RULES : settings.difficulty
}

/**
 * Which rulesets the setup screen offers: the four presets, and the fork while it is in reach.
 *
 * **In reach means the panel is open.** The fifth chip used to appear whenever a fork was
 * *stored*, which is how somebody who had finished with nerd mode was still offered it with the
 * checkbox unticked; and pressing it put them back on rules they could no longer see. Nerd mode is
 * where the fork lives, so the fork is offered where nerd mode is.
 *
 * Here rather than in `GameSetup`, so that this and `rulesetOf` cannot disagree about whether the
 * lit chip is one of the ones on offer.
 */
export function offeredRulesets(settings: Settings): readonly Ruleset[] {
  if (!settings.nerdMode || !hasCustomRules(settings)) return DIFFICULTY_NAMES
  return [...DIFFICULTY_NAMES, CUSTOM_RULES]
}

/**
 * Opens or closes the nerd panel, and closing it puts the presets back in charge.
 *
 * The three facts here were tangled, and the tangle cost a leaderboard place. `nerdMode` shows the
 * panel; `custom` says the forked rules are the ones in play; `overrides` is the fork. Closing the
 * panel used to touch only the first, so the checkbox read as off while the game carried on being
 * played on the fork -- not canonical, refused a place on a board, and nothing on screen saying
 * so.
 *
 * `overrides` still outlives all of it, which is the point of keeping them apart: turning the
 * panel back on and pressing the fork's own chip restores the numbers rather than asking for them
 * again.
 */
export function withNerdMode(settings: Settings, nerdMode: boolean): Settings {
  if (nerdMode) return { ...settings, nerdMode }
  return { ...settings, nerdMode, custom: false }
}

/**
 * Chooses a ruleset. A preset does not discard the custom one, it just stops applying it,
 * so the player can go back to their own numbers without retyping them.
 */
export function withRuleset(settings: Settings, ruleset: Ruleset): Settings {
  if (ruleset === CUSTOM_RULES) return { ...settings, custom: true }
  return { ...settings, difficulty: ruleset, custom: false }
}

/** Editing a rule in nerd mode forks to the custom ruleset; that is what makes it custom. */
export function withOverride(settings: Settings, overrides: Partial<GameConfig>): Settings {
  return { ...settings, custom: true, overrides: { ...settings.overrides, ...overrides } }
}

export function configOf(settings: Settings): GameConfig {
  // Language reaches the engine as part of the ruleset, because the word floor a board has to
  // clear depends on how many words the language's dictionary admits.
  const rules = settings.custom ? settings.overrides : {}
  return configFor(settings.difficulty, { ...rules, language: settings.gameLanguage })
}

/**
 * True when the rules are a published preset, which is what ranking requires.
 *
 * Asks what the rules are, not how they were reached. A game played on numbers identical to a
 * preset is a game on that preset and ranks, even if the player got there through nerd mode.
 */
export function isCanonical(settings: Settings): boolean {
  return !settings.custom || !hasCustomRules(settings)
}

const STORAGE_KEY = 'blinkered.settings.v1'

export function defaultSettings(): Settings {
  const guess = guessLanguage()
  return {
    difficulty: 'medium',
    custom: false,
    overrides: {},
    keyScheme: 'cycle',
    nerdMode: false,
    gameLanguage: guess,
    uiLanguage: guess,
    tutorialSeen: false,
    theme: 'traditional',
  }
}

export function loadSettings(): Settings {
  const fallback = defaultSettings()
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return fallback
    const parsed = JSON.parse(raw) as Partial<Settings>
    const difficulty = DIFFICULTY_NAMES.find((name) => name === parsed.difficulty)
    const keyScheme = KEY_SCHEMES.find((scheme) => scheme === parsed.keyScheme)
    // A stored interface language we no longer translate falls back rather than blanking the
    // interface. The game language is checked against the catalogue instead, once it loads:
    // whether a list exists is a fact about the deployment, not about this file.
    const uiLanguage = localeFor(parsed.uiLanguage ?? '')?.tag
    const overrides = typeof parsed.overrides === 'object' ? { ...parsed.overrides } : {}
    return {
      difficulty: difficulty ?? fallback.difficulty,
      /*
       * A stored `custom` flag means nothing without the rules it refers to, and nothing without
       * the panel those rules are edited in.
       *
       * The second half repairs a state this app could previously get into and then save: closing
       * the panel left `custom` set, so the fork stayed in play with nothing on screen admitting
       * it, and games came out not canonical. Fixed at the toggle, and enforced again here,
       * because a browser that stored the bad state would otherwise restore it on the next load.
       */
      custom:
        parsed.custom === true && parsed.nerdMode === true && Object.keys(overrides).length > 0,
      overrides,
      keyScheme: keyScheme ?? fallback.keyScheme,
      nerdMode: parsed.nerdMode === true,
      gameLanguage: parsed.gameLanguage ?? fallback.gameLanguage,
      uiLanguage: uiLanguage ?? fallback.uiLanguage,
      // Anything but a stored `true` means show it: a corrupt value should err towards offering
      // the tour, which costs one click, rather than hiding it from somebody who has never played.
      tutorialSeen: parsed.tutorialSeen === true,
      // An unknown palette is the traditional one rather than an unstyled page.
      theme: THEMES.find((name) => name === parsed.theme) ?? fallback.theme,
    }
  } catch {
    // A corrupt or unavailable store is not worth failing a game over.
    return fallback
  }
}

/**
 * Puts the chosen palette on `<html>`, where the stylesheet's two theme blocks are keyed.
 *
 * The attribute is absent for the traditional theme rather than set to `traditional`, because
 * that theme is `:root` itself: a page with no attribute at all has to be the game as it was, and
 * that includes a page this function has never run on.
 *
 * `theme-color` moves with it. On iOS that is the status bar over a `black-translucent` web app,
 * so leaving it at the dark value would put dark-on-dark text above a daylight page.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'traditional') delete root.dataset.theme
  else root.dataset.theme = theme
  const bar = document.querySelector('meta[name="theme-color"]')
  bar?.setAttribute('content', BAR_COLOURS[theme])
}

/** Kept beside the palettes they come from; `themes.test.ts` checks they are the real values. */
const BAR_COLOURS: Record<Theme, string> = {
  traditional: '#0e1116',
  light: '#f6f8fa',
  contrast: '#000000',
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // Private browsing and blocked storage are both fine; settings simply do not persist.
  }
}
